import {RouterNetworkTree} from "../../../../../../services/back/infrastructure-schema.service";
import {Server} from "../../../../../../services/model/server.model";

// Types pour la structure de sortie
export interface RouterWithPlacement {
	id: string;
	name: string;
	leftSize: number;
	rightSize: number;
}

export interface ConnectedNetworkOutput {
	id: string;
	name: string;
	connectedRouters: RouterNodeOutput[];
}

export interface RouterNodeOutput {
	id: string;
	name: string;
	leftSize: number;
	rightSize: number;
	connectedIntercoNetwork: ConnectedNetworkOutput[];
}

export interface ConnectedNetworkOutput {
	id: string;
	name: string;
	connectedRouters: RouterNodeOutput[];
	maxLeft: number;
	maxRight: number;
}

/**
 * Fonction principale qui traite le JSON d'entrée et calcule les empattements
 * @param inputJson Arborescence de routeurs, réseaux et serveurs
 * @returns Structure similaire avec les valeurs leftSize et rightSize calculées
 */
export function calculateRouterPlacements(inputJson: RouterNetworkTree[]): RouterNodeOutput[] {
	const result: RouterNodeOutput[] = [];

	for (const routerNode of inputJson) {
		result.push(processRouterNode(routerNode));
	}

	return result;
}

/**
 * * Calcule l'empattement maximal à gauche et à droite pour un réseau d'interconnexion
 * @param connectedRouters Liste des routeurs connectés à ce réseau d'interco
 * @returns Empattement maximal à gauche et à droite
 */
function calculateIntercoMaxSpacing(connectedRouters: RouterNodeOutput[]): { maxLeft: number, maxRight: number } {
	if (connectedRouters.length === 0) {
		return { maxLeft: 0, maxRight: 0 };
	}

	// Premier routeur au centre
	const firstRouter = connectedRouters[0];
	let firstRouterLeftSpacing = firstRouter.leftSize;
	let firstRouterRightSpacing = firstRouter.rightSize;

	// Si un seul routeur, ses propres spacing définissent le max
	if (connectedRouters.length === 1) {
		// Taille minimale (-9/9)
		if (firstRouterLeftSpacing > -7) {
			firstRouterLeftSpacing = -8
		}
		if (firstRouterRightSpacing < 7) {
			firstRouterRightSpacing = 8
		}
		return {
			maxLeft: firstRouterLeftSpacing,
			maxRight: firstRouterRightSpacing
		};
	}

	// Séparation des routeurs restants (après le premier)
	const remainingRouters = connectedRouters.slice(1);
	const isEven = remainingRouters.length % 2 === 0;

	let leftRouters: RouterNodeOutput[];
	let rightRouters: RouterNodeOutput[];

	if (isEven) {
		// Moitié à gauche, moitié à droite
		const midIndex = remainingRouters.length / 2;
		leftRouters = remainingRouters.slice(0, midIndex);
		rightRouters = remainingRouters.slice(midIndex);
	} else {
		// Si impair, moitié+1 à gauche, reste à droite
		const leftCount = Math.ceil(remainingRouters.length / 2);
		leftRouters = remainingRouters.slice(0, leftCount);
		rightRouters = remainingRouters.slice(leftCount);
	}

	// Calcul du maxRight
	let rightSpacing = firstRouterRightSpacing;
	if (rightRouters.length > 0) {
		let sumRightRouterWidths = 0;
		for (const router of rightRouters) {
			sumRightRouterWidths += Math.abs(router.leftSize) + Math.abs(router.rightSize);
		}
		// Ajouter 2 d'écart entre chaque routeur à droite
		rightSpacing += sumRightRouterWidths + 1 * rightRouters.length;
	}

	// Calcul du maxLeft
	let leftSpacing = firstRouterLeftSpacing;
	if (leftRouters.length > 0) {
		let sumLeftRouterWidths = 0;
		for (const router of leftRouters) {
			sumLeftRouterWidths += Math.abs(router.leftSize) + Math.abs(router.rightSize);
		}
		// Ajouter 2 d'écart entre chaque routeur à gauche
		leftSpacing -= (sumLeftRouterWidths + 1 * leftRouters.length);
	}

	return { maxLeft: leftSpacing, maxRight: rightSpacing };
}

/**
 * Étend l'empattement d'un routeur en fonction de ses réseaux d'interco
 * @param router Routeur dont on veut étendre l'empattement
 * @returns Empattement étendu
 */
function calculateExtendedRouterSpacing(router: RouterNodeOutput): { leftSize: number, rightSize: number } {
	let extendedLeftSize = router.leftSize;
	let extendedRightSize = router.rightSize;

	// Si le routeur a plusieurs réseaux d'interco, leur position est calculée comme celle des routeurs
	if (router.connectedIntercoNetwork.length > 1) {
		// Premier réseau au centre
		const firstInterco = router.connectedIntercoNetwork[0];
		const remainingIntercos = router.connectedIntercoNetwork.slice(1);
		const isEven = remainingIntercos.length % 2 === 0;

		let leftIntercos: ConnectedNetworkOutput[];
		let rightIntercos: ConnectedNetworkOutput[];

		if (isEven) {
			// Moitié à gauche, moitié à droite
			const midIndex = remainingIntercos.length / 2;
			rightIntercos = remainingIntercos.slice(0, midIndex);
			leftIntercos = remainingIntercos.slice(midIndex);
		} else {
			// Si impair, moitié+1 à gauche, reste à droite
			const leftCount = Math.ceil(remainingIntercos.length / 2);
			rightIntercos = remainingIntercos.slice(0, leftCount);
			leftIntercos = remainingIntercos.slice(leftCount);
		}

		// // Vérifier si l'empattement des réseaux dépasse celui du routeur
		// if (firstInterco.maxLeft !== undefined && firstInterco.maxLeft < extendedLeftSize) {
		// 	extendedLeftSize = firstInterco.maxLeft;
		// }
		// if (firstInterco.maxRight !== undefined && firstInterco.maxRight > extendedRightSize) {
		// 	extendedRightSize = firstInterco.maxRight;
		// }

		// Ajouter largeur des réseaux à droite
		let rightSpacing = 0;
		for (const interco of rightIntercos) {
			if (interco.maxLeft !== undefined && interco.maxRight !== undefined) {
				rightSpacing += Math.abs(interco.maxLeft) + Math.abs(interco.maxRight);
			}
		}
		rightSpacing += firstInterco.maxRight
		if (rightSpacing > 0) {
			// rightSpacing += 2 * rightIntercos.length; // Ajouter 2 d'écart entre chaque réseau
			// rightSpacing -= 2
			if ( rightSpacing > extendedRightSize) {
				extendedRightSize = rightSpacing;
			}
		}

		// Ajouter largeur des réseaux à gauche
		let leftSpacing = 0;
		for (const interco of leftIntercos) {
			if (interco.maxLeft !== undefined && interco.maxRight !== undefined) {
				leftSpacing -= Math.abs(interco.maxLeft) + Math.abs(interco.maxRight);
			}
		}
		leftSpacing += firstInterco.maxLeft
		if (leftSpacing < 0) {
			// leftSpacing -= 2 * leftIntercos.length; // Ajouter 2 d'écart entre chaque réseau
			// leftSpacing -= 2
			if (leftSpacing < extendedLeftSize) {
				extendedLeftSize = leftSpacing;
			}
		}

	} else if (router.connectedIntercoNetwork.length === 1) {
		// Si un seul réseau d'interco, étendre directement l'empattement
		const interco = router.connectedIntercoNetwork[0];
		if (interco.maxLeft !== undefined && interco.maxLeft < extendedLeftSize) {
			extendedLeftSize = interco.maxLeft;
		}
		if (interco.maxRight !== undefined && interco.maxRight > extendedRightSize) {
			extendedRightSize = interco.maxRight;
		}
	}

	return { leftSize: extendedLeftSize, rightSize: extendedRightSize };
}

/**
 * Traite un nœud de routeur et calcule son empattement
 * @param routerNode Nœud de routeur à traiter
 * @returns Nœud de routeur avec empattement calculé
 */
function processRouterNode(routerNode: RouterNetworkTree): RouterNodeOutput {
	const routerOutput: RouterNodeOutput = {
		id: routerNode.router.id,
		name: routerNode.router.name,
		leftSize: 0,
		rightSize: 0,
		connectedIntercoNetwork: []
	};

	// Calculer l'empattement
	const { leftSize, rightSize } = calculateRouterSize(routerNode);
	routerOutput.leftSize = leftSize;
	routerOutput.rightSize = rightSize;

	// Traiter les réseaux d'intercommunication
	const intercoNetworks = routerNode.connectedNetworks.filter(
		network => network.network.network.role === 'intercommunication'
	);

	for (const intercoNetwork of intercoNetworks) {
		const intercoOutput: ConnectedNetworkOutput = {
			id: intercoNetwork.network.network.id,
			name: intercoNetwork.network.network.name,
			connectedRouters: [],
			maxLeft: 0,
			maxRight: 0,
		};

		// Traiter les routeurs connectés au réseau d'intercommunication
		for (const connectedRouter of intercoNetwork.connectedRouters) {
			intercoOutput.connectedRouters.push(processRouterNode(connectedRouter));
		}

		// Calculer maxLeft et maxRight pour le réseau d'interconnexion
		if (intercoOutput.connectedRouters.length > 0) {
			let { maxLeft, maxRight } = calculateIntercoMaxSpacing(intercoOutput.connectedRouters);
			intercoOutput.maxLeft = maxLeft;
			intercoOutput.maxRight = maxRight;
		}

		routerOutput.connectedIntercoNetwork.push(intercoOutput);
	}

	// Étendre leftSize et rightSize en fonction des réseaux d'interco du routeur
	if (routerOutput.connectedIntercoNetwork.length > 0) {
		const extendedSpacing = calculateExtendedRouterSpacing(routerOutput);
		routerOutput.leftSize = extendedSpacing.leftSize;
		routerOutput.rightSize = extendedSpacing.rightSize;
	}

	return routerOutput;
}

/**
 * Calcule la taille d'un réseau en fonction du nombre de serveurs
 * @param servers Liste des serveurs
 * @returns Taille du réseau
 */
function getNetworkSize(servers: Server[]): number {
	const count = servers.length;

	if (count === 0 || count === 1) {
		return 8;
	} else if (count === 2) {
		return 12;
	} else if (count === 3) {
		return 16;
	} else {
		return 20; // 4 serveurs ou plus
	}
}

/**
 * Calcule l'empattement d'un routeur selon les règles spécifiées
 * @param routerNode Nœud de routeur
 * @returns Valeurs leftSize et rightSize calculées
 */
function calculateRouterSize(routerNode: RouterNetworkTree): { leftSize: number, rightSize: number } {
	// Filtrer les réseaux locaux
	const localNetworks = routerNode.connectedNetworks.filter(
		network => network.network.network.role != 'intercommunication'
	);

	// Si pas de réseaux locaux, l'empattement est fixe: -5 à gauche, 5 à droite
	if (localNetworks.length === 0) {
		return { leftSize: -4, rightSize: 4 };
	}

	// Calcul des tailles des réseaux
	const networkSizes = localNetworks.map(network =>
		getNetworkSize(network.network.servers)
	);

	// Déterminer si le nombre de réseaux locaux est pair ou impair
	const isEven = networkSizes.length % 2 === 0;

	if (isEven) {
		// Cas pair: diviser les réseaux en deux parties égales
		const midIndex = Math.floor(networkSizes.length / 2);

		let rightSum = 0;
		for (let i = 0; i < midIndex; i++) {
			rightSum += networkSizes[i];
			// if (i >= 0) rightSum += 2; // Ajouter 2 pour chaque espace entre réseaux
		}

		let leftSum = 0;
		for (let i = midIndex; i < networkSizes.length; i++) {
			leftSum += networkSizes[i];
			// if (i >= midIndex) leftSum += 2; // Ajouter 2 pour chaque espace entre réseaux
		}

		return { leftSize: -4 - leftSum, rightSize: 4 + rightSum };
	} else {
		// Cas impair

		// Vérifier s'il y a des réseaux d'intercommunication
		const hasInterco = routerNode.connectedNetworks.some(
			network => network.network.network.role === 'intercommunication'
		);

		if (hasInterco) {
			// Avec interco, base fixe de +5/-5, puis répartition (avec 1 de plus à droite)
			const leftCount = Math.ceil(networkSizes.length / 2);
			// const rightCount = Math.ceil(networkSizes.length / 2);

			let rightSum = 0;
			for (let i = 0; i < leftCount; i++) {
				rightSum += networkSizes[i];
				// if (i >= 0) rightSum += 2; // Ajouter 2 pour chaque espace entre réseaux
			}

			let leftSum = 0;
			for (let i = leftCount; i < networkSizes.length; i++) {
				leftSum += networkSizes[i];
				// if (i >= leftCount) leftSum += 2; // Ajouter 2 pour chaque espace entre réseaux
			}

			return { leftSize: -4 - leftSum, rightSize: 4 + rightSum };
		} else {
			// Sans interco, le premier réseau détermine un décalage de base
			const firstNetworkSize = networkSizes[0];
			const baseOffset = (firstNetworkSize) / 2;
			// Répartir les réseaux restants
			const remaining = networkSizes.slice(1);
			const leftCount = Math.floor(remaining.length / 2);
			// const rightCount = Math.ceil(remaining.length / 2);

			let rightSum = 0;
			for (let i = 0; i < leftCount; i++) {
				rightSum += remaining[i];
				// if (i >= 0) rightSum += 2; // Ajouter 2 pour chaque espace entre réseaux
			}

			let leftSum = 0;
			for (let i = leftCount; i < remaining.length; i++) {
				leftSum += remaining[i];
				// if (i >= leftCount) leftSum += 2; // Ajouter 2 pour chaque espace entre réseaux
			}

			return { leftSize: -baseOffset - leftSum, rightSize: baseOffset + rightSum };
		}
	}
}

export function CalculateTotalSize(
	dataguide: RouterNodeOutput[],
	levelArray: { [depth: number]: number }
): { xMin: number, xMax: number, zMin: number, zMax: number } {

	const first = dataguide[0];
	const remaining = dataguide.slice(1);
	const isEven = remaining.length % 2 === 0;

	let left: RouterNodeOutput[];
	let right: RouterNodeOutput[];

	if (isEven) {
		// Moitié à gauche, moitié à droite
		const midIndex = remaining.length / 2;
		right = remaining.slice(0, midIndex);
		left = remaining.slice(midIndex);
	} else {
		// Si impair, moitié+1 à gauche, reste à droite
		const leftCount = Math.floor(remaining.length / 2);
		right = remaining.slice(0, leftCount);
		left = remaining.slice(leftCount);
	}

	let xMax = first.rightSize
	for (const element of right) {
		xMax += (-element.leftSize) + element.rightSize;
	}

	let xMin = first.leftSize
	for (const element of left) {
		xMin -= (-element.leftSize) - element.leftSize;
	}

	// Find zMax
	const keys = Object.keys(levelArray).map(Number);

	const lastKey = Math.max(...keys);
	const lastValue = levelArray[lastKey];

	return {xMin: xMin - 40, xMax: xMax + 40, zMin: - 20, zMax: lastValue + 20}
}
